import { AnyAction } from "redux"
import { Observable, of } from "rxjs"
import { AjaxError } from "rxjs/ajax"
import { isActionOf } from "typesafe-actions"
import { catchError, filter, map, switchMap, withLatestFrom } from "rxjs/operators"
import { ActionsObservable, StateObservable } from "redux-observable"

// Models
import { Record, Signal } from "../../models"
import { DataResponse } from "../../../http/models"
import { EpicDependencies, RootState } from "../../../../models"

// Actions
import * as a from "../actions"
import * as wsActions from "../../../ws/store/actions"

// Selectors
import { selectCurrentRecord, selectReceiveNext } from "../selectors"

/**
 * @param action$
 * @param _
 * @param d
 */
export const fetchNextRecordEpic = (action$: ActionsObservable<AnyAction>, _: StateObservable<RootState>, d: EpicDependencies) => {
    return action$.pipe(
        filter(isActionOf(a.fetchNextRecordAsync.request)),
        switchMap(() => d.recordsService.fetchNext().pipe(
            switchMap(response => {
                const { data }: DataResponse<Record> = response.response;

                return of(
                    a.fetchNextRecordAsync.success(data),
                    a.fetchRecordSignalAsync.request(data.teleRecordId),
                )
            }),
            catchError(handleRecordErrorResponse),
        )),
    )
};

/**
 * @param action$
 * @param _
 * @param d
 */
export const fetchNextByIdRecordEpic = (action$: ActionsObservable<AnyAction>, _: StateObservable<RootState>, d: EpicDependencies) => {
    return action$.pipe(
        filter(isActionOf(a.fetchNextRecordByIdAsync.request)),
        switchMap(({ payload }) => d.recordsService.fetchNextById(payload).pipe(
            switchMap(response => {
                const { data }: DataResponse<Record> = response.response;

                return of(
                    a.fetchNextRecordByIdAsync.success(data),
                    a.fetchRecordSignalAsync.request(data.teleRecordId),
                )
            }),
            catchError(handleRecordErrorResponse),
        )),
    )
};

/**
 * @param action$
 * @param _
 * @param d
 */
 export const fetchNextInProgressByIdRecordEpic = (action$: ActionsObservable<AnyAction>, _: StateObservable<RootState>, d: EpicDependencies) => {
    return action$.pipe(
        filter(isActionOf(a.fetchInProgressNextRecordByIdAsync.request)),
        switchMap(({ payload }) => d.recordsService.fetchNextInprogressById(payload).pipe(
            switchMap(response => {
                const { data }: DataResponse<Record> = response.response;

                return of(
                    a.fetchInProgressNextRecordByIdAsync.success(data),
                    a.fetchRecordSignalAsync.request(data.teleRecordId),
                )
            }),
            catchError(handleRecordErrorResponse),
        )),
    )
};


/**
 * @param action$
 * @param _
 * @param d
 */
export const fetchRecordByIdEpic = (action$: ActionsObservable<AnyAction>, _: StateObservable<RootState>, d: EpicDependencies) => {
    return action$.pipe(
        filter(isActionOf(a.fetchRecordByIdAsync.request)),
        switchMap(({ payload: id }) => d.recordsService.fetchById(id).pipe(
            switchMap(response => {
                const { data }: DataResponse<Record> = response.response;

                return of(
                    a.fetchRecordByIdAsync.success(data),
                    a.fetchRecordSignalAsync.request(data.teleRecordId),
                )
            }),
            catchError(handleRecordErrorResponse),
        )),
    )
};

/**
 * @param action$
 * @param _
 * @param d
 */
export const fetchRecordSignalEpic = (action$: ActionsObservable<AnyAction>, _: StateObservable<RootState>, d: EpicDependencies) => {
    return action$.pipe(
        filter(isActionOf(a.fetchRecordSignalAsync.request)),
        switchMap(({ payload: id }) => d.recordsService.fetchRecordSignal(id).pipe(
            map(response => {
                const { data }: DataResponse<Array<Signal>> = response.response;
                const reduced = data.reduce((byLead, signal) => {
                    return {
                        ...byLead,
                        [signal.lead]: signal,
                    }
                }, {});

                return a.fetchRecordSignalAsync.success(reduced)
            }),
            catchError(err => of(a.fetchRecordSignalAsync.failure(err))),
        )),
    )
};

/**
 * @param action$
 * @param state$
 * @param d
 */
export const updateRecordConclusionEpic = (action$: ActionsObservable<AnyAction>, state$: StateObservable<RootState>, d: EpicDependencies) => {
    return action$.pipe(
        filter(isActionOf(a.updateRecordConclusionAsync.request)),
        withLatestFrom(state$),
        switchMap(([{ payload: conclusion }, state]) => {
            const record = selectCurrentRecord(state);

            if (record === null) {
                return of(a.updateRecordConclusionAsync.failure("Current record is null"));
            }

            return d.recordsService.updateConclusion(conclusion, record.teleRecordId).pipe(
                map(({ response }) => {
                    const { data: { conclusion } }: DataResponse<Record> = response;

                    return a.updateRecordConclusionAsync.success(conclusion)
                }),
                catchError(err => {
                    if (err.status === 409) {
                        return of(
                            a.recordConflict(),
                            a.updateRecordConclusionAsync.failure({ status: err.status, message: "Conflict" })
                        )
                    }

                    return of(a.updateRecordConclusionAsync.failure({
                        status: err.status,
                        message: err.response?.message || "An error occurred"
                    }))
                }),
            )
        }),
    )
};

/**
 * @param action$
 * @param state$
 * @param d
 */
export const approveRecordEpic = (action$: ActionsObservable<AnyAction>, state$: StateObservable<RootState>, d: EpicDependencies) => {
    return action$.pipe(
        filter(isActionOf(a.approveRecordAsync.request)),
        withLatestFrom(state$),
        switchMap(([{ payload: id }, state]) => d.recordsService.approveRecord(id).pipe(
            switchMap(() => {
                const actions: Array<AnyAction> = [a.approveRecordAsync.success()];
                const receiveNext = selectReceiveNext(state);

                if (receiveNext) {
                    actions.push(a.fetchNextRecordAsync.request())
                }

                return of(...actions)
            }),
            catchError(err => {
                if (err.status === 409) {
                    return of(
                        a.recordConflict(),
                        a.approveRecordAsync.failure({ status: err.status, message: "Conflict" })
                    )
                }

                return of(a.approveRecordAsync.failure({ status: err.status, message: err.response?.message || "An error occurred" }))
            }),
        )),
    )
};

/**
 * @param action$
 */
export const sendUserInactiveEpic = (action$: ActionsObservable<AnyAction>) => {
    return action$.pipe(
        filter(isActionOf(a.sendUserInactive)),
        map(() => wsActions.closeSingleRecordWSConnection({ code: 4000, reason: "User was inactive." })),
    )
};

/**
 * @param error
 */
function handleRecordErrorResponse(error: AjaxError): Observable<AnyAction> {
    const { status, response } = error;

    if (response && typeof response.message !== "undefined") {
        return of(a.fetchNextRecordAsync.failure({
            status,
            message: response.message,
        }))
    }

    return of(a.fetchNextRecordAsync.failure({ status }))
}

/**
 * @param action$
 * @param state$
 * @param d
 */
export const updateRecordParametersEpic = (action$: ActionsObservable<AnyAction>, state$: StateObservable<RootState>, d: EpicDependencies) => {
    return action$.pipe(
        filter(isActionOf(a.updateRecordParametersAsync.request)),
        withLatestFrom(state$),
        switchMap(([{ payload: params }, state]) => {
            const record = selectCurrentRecord(state);

            if (record === null) {
                return of(a.updateRecordParametersAsync.failure("Current record is null"));
            }

            return d.recordsService.updateParameters(params, record.teleRecordId).pipe(
                map(({ response }) => {
                    const { data } = response;

                    return a.updateRecordParametersAsync.success(data)
                }),
                catchError(err => {
                    if (err.status === 409) {
                        return of(
                            a.recordConflict(),
                            a.updateRecordParametersAsync.failure({ status: err.status, message: "Conflict" })
                        )
                    }

                    return of(a.updateRecordParametersAsync.failure({
                        status: err.status,
                        message: err.response?.message || "An error occurred"
                    }))
                }),
            )
        }),
    )
};

/**
 * @param action$
 * @param state$
 * @param d
 */
export const updateRecordInterpretationEpic = (action$: ActionsObservable<AnyAction>, state$: StateObservable<RootState>, d: EpicDependencies) => {
    return action$.pipe(
        filter(isActionOf(a.updateRecordInterpretationsAsync.request)),
        withLatestFrom(state$),
        switchMap(([{ payload: interpretation }, state]) => {
            const record = selectCurrentRecord(state);

            if (record === null) {
                return of(a.updateRecordInterpretationsAsync.failure("Current record is null"));
            }

            return d.recordsService.updateInterpretation(interpretation, record.teleRecordId).pipe(
                map(({ response }) => {
                    const { data: { interpretation } }: DataResponse<Record> = response;

                    return a.updateRecordInterpretationsAsync.success(interpretation)
                }),
                catchError(err => {
                    if (err.status === 409) {
                        return of(
                            a.recordConflict(),
                            a.updateRecordInterpretationsAsync.failure({ status: err.status, message: "Conflict" })
                        )
                    }

                    return of(a.updateRecordInterpretationsAsync.failure({
                        status: err.status,
                        message: err.response?.message || "An error occurred"
                    }))
                }),
            )
        }),
    )
};

/**
 * @param action$
 * @param _
 * @param d
 */
export const fetchPDFRecordEpic = (action$: ActionsObservable<AnyAction>, _: StateObservable<RootState>, d: EpicDependencies) => {
    return action$.pipe(
        filter(isActionOf(a.fetchPDFRecordAsync.request)),
        switchMap(({ payload }) => d.recordsService.fetchPDF(payload).pipe(
            map((response) => {
                const base64 = btoa(
                    new Uint8Array(response.response).reduce((data, byte) => data + String.fromCharCode(byte), '')
                );
                return a.fetchPDFRecordAsync.success(base64);
            }),
            catchError((err) => {
                return of(a.fetchPDFRecordAsync.failure(err.response))
            }),
        )),
    )
};
